import { createSlice } from '@reduxjs/toolkit'
import { getUrlParameter, SS } from 'utils/common'
import jwt_decode from 'jwt-decode'
import { color as defaultColor } from 'components/Theming/Colors/default'
import { color as mustoColor } from 'components/Theming/Colors/musto'
import { color as royalColor } from 'components/Theming/Colors/royal_design'
import { color as asketColor } from 'components/Theming/Colors/asket'
import { color as bjornborgColor } from 'components/Theming/Colors/bjornborg'
import { color as totemeColor } from 'components/Theming/Colors/toteme'
import { color as marysiaColor } from 'components/Theming/Colors/marysia'
import { color as douchebagsColor } from 'components/Theming/Colors/douchebags'
import { color as woodwoodColor } from 'components/Theming/Colors/woodwood'
import { color as aycaneColor } from 'components/Theming/Colors/aycane'
import { color as bymalinaColor } from 'components/Theming/Colors/bymalina'
import { color as eightyEightyFourtyEightyColor } from 'components/Theming/Colors/8848'
import { color as henryLloydColor } from 'components/Theming/Colors/henrylloyd'
import { color as hhColor } from 'components/Theming/Colors/hh'
import { color as chimiColor } from 'components/Theming/Colors/chimi'
import { color as ogoshipColor } from 'components/Theming/Colors/ogoship'
import { color as snsColor } from 'components/Theming/Colors/sns'
import { fonts as defaultFonts } from 'components/Theming/Fonts/default'
import { fonts as mustoFonts } from 'components/Theming/Fonts/musto'
import { fonts as royalFonts } from 'components/Theming/Fonts/royal_design'
import { fonts as asketFonts } from 'components/Theming/Fonts/asket'
import { fonts as bjornborgFonts } from 'components/Theming/Fonts/bjornborg'
import { fonts as totemeFonts } from 'components/Theming/Fonts/toteme'
import { fonts as marysiaFonts } from 'components/Theming/Fonts/marysia'
import { fonts as douchebagsFonts } from 'components/Theming/Fonts/douchebags'
import { fonts as woodwoodFonts } from 'components/Theming/Fonts/woodwood'
import { fonts as aycaneFonts } from 'components/Theming/Fonts/aycane'
import { fonts as bymalinaFonts } from 'components/Theming/Fonts/bymalina'
import { fonts as eightyEightyFourtyEightyFonts } from 'components/Theming/Fonts/8848'
import { fonts as henryLloydFonts } from 'components/Theming/Fonts/henrylloyd'
import { fonts as hhFonts } from 'components/Theming/Fonts/hh'
import { fonts as chimiFonts } from 'components/Theming/Fonts/chimi'
import { fonts as ogoshipFonts } from 'components/Theming/Fonts/ogoship'
import { fonts as snsFonts } from 'components/Theming/Fonts/sns'
import { setToken } from '../utils/auth'

let themingFonts = {
	default: defaultFonts,
	musto: mustoFonts,
	royal_design: royalFonts,
	asket: asketFonts,
	bjornborg: bjornborgFonts,
	toteme: totemeFonts,
	marysia: marysiaFonts,
	douchebags: douchebagsFonts,
	woodwood: woodwoodFonts,
	aycane: aycaneFonts,
	bymalina: bymalinaFonts,
	[8848]: eightyEightyFourtyEightyFonts,
	henrylloyd: henryLloydFonts,
	hh: hhFonts,
	chimi: chimiFonts,
	ogoship: ogoshipFonts,
	sns: snsFonts,
}

let themingColors = {
	default: defaultColor,
	musto: mustoColor,
	royal_design: royalColor,
	asket: asketColor,
	bjornborg: bjornborgColor,
	toteme: totemeColor,
	marysia: marysiaColor,
	douchebags: douchebagsColor,
	woodwood: woodwoodColor,
	aycane: aycaneColor,
	bymalina: bymalinaColor,
	[8848]: eightyEightyFourtyEightyColor,
	henrylloyd: henryLloydColor,
	hh: hhColor,
	chimi: chimiColor,
	ogoship: ogoshipColor,
	sns: snsColor,
}

/**
 * getTheme {Funciton}
 * @description - get current theme;
 * @return {Object}
 *
 * */
const getTheme = () => {
	let shopProperties = SS.get('shopProperties') || null
	let themeName =
		shopProperties && shopProperties['ui.theme']
			? shopProperties['ui.theme'].toLowerCase()
			: 'default'
	let themeCurrentName =
		themingFonts[themeName] && themingColors[themeName]
			? themeName
			: 'default'
	let theme = {
		...themingFonts[themeCurrentName],
		color: themingColors[themeCurrentName],
		name: themeCurrentName,
	}

	return theme
}

export const rootSlice = createSlice({
	name: 'root',
	initialState: {
		isLoading: false,
		user: {},
		themes: {
			default: themingFonts['default'],
			musto: themingFonts['musto'],
			royal_design: themingFonts['royal_design'],
			asket: themingFonts['asket'],
			bjornborg: themingFonts['bjornborg'],
			toteme: themingFonts['toteme'],
			marysia: themingFonts['marysia'],
			douchebags: themingFonts['douchebags'],
			woodwood: themingFonts['woodwood'],
			aycane: themingFonts['aycane'],
			bymalina: themingFonts['bymalina'],
			[8848]: themingFonts['8848'],
			henrylloyd: themingFonts['henrylloyd'],
			hh: themingFonts['hh'],
			chimi: themingFonts['chimi'],
			ogoship: themingFonts['ogoship'],
			sns: themingFonts['sns'],
		},
		currentTheme: getTheme(),
		wrapperUrl: getUrlParameter('wrapperUrl') || '',
		email: getUrlParameter('email') || '',
		userName: getUrlParameter('user') || '',
		orderId: getUrlParameter('orderId') || null,
		managerUsername: getUrlParameter('managerUsername') || null,
		shopId: getUrlParameter('shopId') || SS.get('shopId') || 18000,
		shopProperties: SS.get('shopProperties') || {},
		alertBottom: SS.get('alertBottom') || 'open',
		modeApp: SS.get('modeApp') || 'default',
		locale: getUrlParameter('language') || SS.get('language') || 'en',
		messages: {},
	},
	reducers: {
		getPropertiesStart: (state) => {
			state.isLoading = true
		},
		getPropertiesSuccess: (state, { payload }) => {
			state.isLoading = false
			let properties = payload.reduce((acc, item) => {
				acc[item.name] = item.value
				return acc
			}, {})
			const currentThemeName = properties['ui.theme']?.toLowerCase()
			const themeName =
				themingFonts[currentThemeName] &&
				themingColors[currentThemeName]
					? currentThemeName
					: 'default'
			const uiTheme = state?.themes[themeName]
			state.shopProperties = properties
			state.currentTheme = {
				...uiTheme,
				color: themingColors[themeName],
				name: themeName,
			}
			SS.set('shopProperties', state.shopProperties)
		},
		getPropertiesError: (state) => {
			state.isLoading = false
		},

		getLanguageProviderStart: (state) => {
			state.isLoading = true
		},
		getLanguageProviderSuccess: (
			state,
			{ payload: { currentLang, messages } }
		) => {
			state.isLoading = false
			state.locale = currentLang
			state.messages[currentLang] = messages
		},
		getLanguageProviderError: (state) => {
			state.isLoading = false
		},

		setLanguage: (state, { payload }) => {
			state.locale = payload
		},
		setDataFromJWTToken: (state, { payload }) => {
			// I parse data from the token and add it to the state
			let { customer = {} } = JSON.parse(jwt_decode(payload)?.sub)
			state.locale = customer.locale
			state.shopId = customer.shopId
			state.email = customer.email
			state.userName = `${customer?.firstName} ${customer?.lastName}`
			state.orderId = customer.id

			SS.set('shopId', customer.shopId)
			SS.set('language', customer.locale)
			setToken(JSON.stringify({ token: payload }))
		},
		setAlertBottomStatus: (state, { payload }) => {
			state.alertBottom = payload
			SS.set('alertBottom', payload)
		},

		clearStateOnLogout: (state) => {
			state.email = null
			state.userName = null
			state.orderId = null
			state.modeApp = 'default'
		},
		setModeApp: (state) => {
			state.modeApp = 'modal'
		},
	},
})

export const {
	getPropertiesStart,
	getPropertiesSuccess,
	getPropertiesError,
	getLanguageProviderStart,
	getLanguageProviderSuccess,
	getLanguageProviderError,
	setAlertBottomStatus,
	setLanguage,
	setDataFromJWTToken,
	clearStateOnLogout,
	setModeApp,
} = rootSlice.actions
export const { reducer } = rootSlice
